/**
 * ADR-0214 Part 2 — one subject (cut 1): Activity first (every action under
 * it, both planes, with what it touched), then what Atlas served, what failed,
 * what was filed and what the person reported. Sections with nothing to show
 * are left out, except knowledge and studies, which say when Atlas had none.
 */

import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { rangeLastDays } from "../components/AiUsageDashboard.js";
import { ActivityTable, Section } from "../components/SubjectTables.js";
import { TrailLinks, useActivityTrail, useWalkedTrail } from "../components/ActivityTrail.js";
import { trailPeriod } from "../lib/activityTrail.js";
import { apiFetch } from "../lib/api.js";
import {
  APPROX_TOKENS_TITLE,
  KNOWLEDGE_HOW_LABEL,
  SEARCH_WHERE_LABEL,
  STATUS_LABEL,
  USAGE_LABEL,
  approxTokens,
  countOrDash,
  filedWhat,
  outcomeWord,
  when,
  type SubjectDetailResponse,
} from "../lib/subjects.js";

export function TenantSubjectDetail() {
  const { number } = useParams();
  const [data, setData] = useState<SubjectDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The period it was opened in (the address's, else the trail's, else the last 30 days), for the person link.
  const [params] = useSearchParams();
  const walkedPeriod = trailPeriod(useWalkedTrail()) ?? rangeLastDays(30);
  const range = { from: params.get("from") || walkedPeriod.from, to: params.get("to") || walkedPeriod.to };
  const trail = useActivityTrail(data?.subject.subject ?? "Subject", [{ to: `/app/activity?from=${range.from}&to=${range.to}`, label: "Activity" }]);
  const breadcrumb = <TrailLinks trail={trail.above} />;

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    apiFetch<SubjectDetailResponse>(`/api/tenant/subjects/${encodeURIComponent(number ?? "")}`)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [number]);

  if (error) {
    return (
      <PageLayout title="Subject" breadcrumb={breadcrumb}>
        <ErrorState detail={error === "not_available" ? "This subject is not available." : error} />
      </PageLayout>
    );
  }
  if (!data) {
    return (
      <PageLayout title="Subject" breadcrumb={breadcrumb}>
        <LoadingState />
      </PageLayout>
    );
  }

  const tz = data.time_zone;
  const s = data.subject;

  return (
    <PageLayout title={s.subject} breadcrumb={breadcrumb} description={s.goal ?? undefined}>
      <div style={{ display: "grid", gap: 12 }}>
        <div className="muted">
          {s.person_key ? (
            <Link to={`/app/subjects/people/${s.person_key}?from=${range.from}&to=${range.to}`} state={trail.onward}>
              {s.person}
            </Link>
          ) : s.person}
          {" · Opened "}
          {when(s.opened_at, tz)}
          {" · "}
          {STATUS_LABEL[s.status]}
          {s.closed_at ? ` · Closed ${when(s.closed_at, tz)}` : ""}
          {" · Asked before: —"}
          {s.redacted ? (
            <>
              {" "}
              <span className="pill pillNeutral">Redacted</span>
            </>
          ) : null}
        </div>

        <Section title="Activity">
          {data.activity.length === 0 ? <div className="muted" style={{ padding: 12 }}>Nothing recorded under this subject.</div> : <ActivityTable rows={data.activity} timeZone={tz} />}
        </Section>
        {data.activity_truncated ? <div className="muted">Showing the first 2,000 actions.</div> : null}

        {data.widened.length > 0 ? (
          <Section title="Widened">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Widened to</th>
                  <th>Goal</th>
                </tr>
              </thead>
              <tbody>
                {data.widened.map((w, i) => (
                  <tr key={i}>
                    <td>{when(w.at, tz)}</td>
                    <td>{w.subject}</td>
                    <td>{w.goal ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {data.picked_up.length > 0 ? (
          <Section title="Picked up">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>From</th>
                  <th>On</th>
                </tr>
              </thead>
              <tbody>
                {data.picked_up.map((p, i) => (
                  <tr key={i}>
                    <td>{when(p.at, tz)}</td>
                    <td>
                      {p.from ? (
                        <Link to={`/app/subjects/${p.from.number}`} state={trail.onward}>
                          {p.from.subject}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{p.object ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        <Section title="Knowledge served">
          {data.knowledge_served.length === 0 && data.knowledge_unnamed === 0 ? (
            <div className="muted" style={{ padding: 12 }}>Atlas had no knowledge for this subject.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>How</th>
                </tr>
              </thead>
              <tbody>
                {data.knowledge_served.map((k) => (
                  <tr key={k.name}>
                    <td>
                      <Link to={`/app/admin/tenant-knowledge/entry/${encodeURIComponent(k.name)}`} state={trail.onward}>
                        {k.name}
                      </Link>
                    </td>
                    <td>{KNOWLEDGE_HOW_LABEL[k.how]}</td>
                  </tr>
                ))}
                {data.knowledge_unnamed > 0 ? (
                  <tr>
                    <td className="muted" colSpan={2}>
                      {data.knowledge_unnamed === 1 ? "1 knowledge entry you can't see" : `${data.knowledge_unnamed.toLocaleString()} knowledge entries you can't see`}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Studies served">
          {data.studies_served.length === 0 && data.studies_unnamed === 0 ? (
            <div className="muted" style={{ padding: 12 }}>Atlas had no prior study for this subject.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Study</th>
                </tr>
              </thead>
              <tbody>
                {data.studies_served.map((st) => (
                  <tr key={st.slug}>
                    <td>
                      <Link to={`/app/studies/${st.slug}`} state={trail.onward}>
                        {st.title}
                      </Link>
                    </td>
                  </tr>
                ))}
                {data.studies_unnamed > 0 ? (
                  <tr>
                    <td className="muted">{data.studies_unnamed === 1 ? "1 study you can't see, or that no longer exists" : `${data.studies_unnamed.toLocaleString()} studies you can't see, or that no longer exist`}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </Section>

        {data.searches.length > 0 ? (
          <Section title="Searches">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Where</th>
                  <th>Words</th>
                  <th>Results</th>
                </tr>
              </thead>
              <tbody>
                {data.searches.map((q, i) => (
                  <tr key={i}>
                    <td>{when(q.at, tz)}</td>
                    <td>{SEARCH_WHERE_LABEL[q.where] ?? q.where}</td>
                    <td>{q.words}</td>
                    <td>{q.results === 0 ? "Found nothing" : q.results.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {data.failed.length > 0 ? (
          <Section title="Failed queries">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Names</th>
                  <th>Later query succeeded</th>
                </tr>
              </thead>
              <tbody>
                {data.failed.map((f, i) => (
                  <tr key={i}>
                    <td>{when(f.at, tz)}</td>
                    <td>{f.names.length ? f.names.join(", ") : "—"}</td>
                    <td>{f.later_succeeded ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {data.not_found.length > 0 ? (
          <Section title="Names not found">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Times</th>
                </tr>
              </thead>
              <tbody>
                {data.not_found.map((n) => (
                  <tr key={n.name}>
                    <td className="mono">{n.name}</td>
                    <td>{n.times.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {data.filed.length > 0 ? (
          <Section title="Filed">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {data.filed.map((f, i) => (
                  <tr key={i}>
                    <td>{when(f.at, tz)}</td>
                    <td>{filedWhat(f)}</td>
                    <td>{outcomeWord(f.outcome)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {data.answer_reports.length > 0 ? (
          <Section title="Answer reports">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Topic</th>
                  <th>Verdict</th>
                  <th>In their words</th>
                </tr>
              </thead>
              <tbody>
                {data.answer_reports.map((a, i) => (
                  <tr key={i}>
                    <td>{when(a.at, tz)}</td>
                    <td>{a.topic}</td>
                    <td>{outcomeWord(a.verdict)}</td>
                    <td>{a.words}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {data.gaps.length > 0 ? (
          <Section title="Gaps reported">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Topic</th>
                  <th>In their words</th>
                </tr>
              </thead>
              <tbody>
                {data.gaps.map((g, i) => (
                  <tr key={i}>
                    <td>{when(g.at, tz)}</td>
                    <td>{g.topic}</td>
                    <td>{g.words}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {data.concerns.length > 0 ? (
          <Section title="Concerns">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Topic</th>
                  <th>Sentiment</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {data.concerns.map((c, i) => (
                  <tr key={i}>
                    <td>{when(c.at, tz)}</td>
                    <td>{c.topic}</td>
                    <td>{outcomeWord(c.sentiment)}</td>
                    <td>{c.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}

        {data.usage.length > 0 ? (
          <Section title="Usage">
            <table className="table">
              <thead>
                <tr>
                  <th>Kind of work</th>
                  <th>Calls</th>
                  <th title={APPROX_TOKENS_TITLE}>≈ Tokens</th>
                  <th>Didn't go through</th>
                </tr>
              </thead>
              <tbody>
                {data.usage.map((u) => (
                  <tr key={u.category}>
                    <td>{USAGE_LABEL[u.category] ?? u.category}</td>
                    <td>{u.calls.toLocaleString()}</td>
                    <td>{approxTokens(u.chars)}</td>
                    <td>{countOrDash(u.did_not_go_through)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : null}
      </div>
    </PageLayout>
  );
}
